import type { FC, HTMLAttributes, ReactNode } from 'react';
import { type DragEvent, useState } from 'react';

import { ReactComponent as UploadCloudIcon } from 'assets/img/upload-cloud.svg';
import classNames from 'classnames';

interface IDragUploadZoneProps extends Omit<HTMLAttributes<HTMLDivElement>, 'className'> {
  onUpload: (file: File) => void;
  className?: string | ((isDragging: boolean) => string);
  formats: string;
  blockClassName?: string;
  description: ReactNode;
}

export const DragUploadZone: FC<IDragUploadZoneProps> = ({
  onUpload,
  className,
  children,
  formats,
  blockClassName,
  description,
  ...props
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const validateFile = (file: File | FileList): boolean => {
    if (file instanceof FileList) {
      return file.length === 1 && validateFile(file[0]);
    }

    const fileExtension = file.name.split('.').pop();

    return formats.includes(`.${fileExtension}`);
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;

    if (!validateFile(files)) return;

    onUpload(files[0]);
  };

  return (
    <div
      className={classNames('relative', typeof className === 'function' ? className(isDragging) : className)}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      {...props}
    >
      {isDragging && (
        <div
          className={classNames(
            'bg-white/85 animate-fade-in [backdrop-filter:blur(35px)] absolute overflow-hidden rounded-2xl z-20 inset-0 border-2 border-dashed border-[#5F30E2]',
            blockClassName
          )}
        >
          <div className='bg-[rgba(95,_48,_226,_0.08)] flex flex-col items-center justify-center gap-[27px] size-full'>
            <UploadCloudIcon className='block text-[#42219E] size-16' />
            <p className='text-[#42219E] text-sm leading-[18px]'>{description}</p>
          </div>
        </div>
      )}
      {children}
    </div>
  );
};
